/**
 * Debug descriptors for every bounding box a shape has — the visual layer's temporary proving
 * ground for `shape-coordinates.ts`.
 *
 * Each box is returned as an explicit closed polygon rather than a rect so the rotated box (a real
 * quadrilateral, not an axis-aligned rect) renders through the same code path as the others.
 *
 * The four boxes together show the whole pipeline for one shape:
 *
 * ```
 *   geometric  ──inset by visual ratios──▶  visual  ──rotate about pivot──▶  rotatedVisual
 *                                                                                  │
 *                                                                       axis-aligned envelope
 *                                                                                  ▼
 *                                                                                aabb
 * ```
 *
 * When a shape is an unrotated rectangle all four coincide exactly — that is correct, not a bug, so
 * the styles below are chosen to stay individually readable when perfectly overlapped.
 */

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type Point,
  type Rect,
  drawableRectOfShape,
  rotatedAabbOfRect,
  rotatedRectCorners,
  rotationPivotOfShape,
} from '@/lib/shapes/shape-coordinates'
import { geometricRectOfShape } from '@/lib/shapes/shape-visual-bounds'
import type { DebugBoundingBoxKind } from './types/debug-bounding-box-kind'
import type { DebugBoundingBox } from './types/debug-bounding-box'

export const DEBUG_BOUNDING_BOX_COLORS: Record<DebugBoundingBoxKind, string> = {
  geometric: '#e11d48',
  visual: '#16a34a',
  rotatedVisual: '#f59e0b',
  aabb: '#2563eb',
}

export const DEBUG_BOUNDING_BOX_LABELS: Record<DebugBoundingBoxKind, string> = {
  geometric: 'Geometric',
  visual: 'Visual',
  rotatedVisual: 'Visual (rotated)',
  aabb: 'AABB',
}

/** Colour for the rotation pivot marker, which is not a box. */
export const DEBUG_PIVOT_COLOR = '#7c3aed'

function rectToPolygon(rect: Rect): Point[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ]
}

/**
 * Every bounding box for `shape`, in draw order (widest-context last so the AABB sits on top).
 *
 * Pure and allocation-light: safe to call for every shape on every frame.
 */
export function debugBoundingBoxesForShape(shape: ShapeData): DebugBoundingBox[] {
  const geometric = geometricRectOfShape(shape)
  const drawable = drawableRectOfShape(shape)
  const pivot = rotationPivotOfShape(shape)
  const rotationDeg = shape.rotation ?? 0

  return [
    {
      kind: 'geometric',
      label: DEBUG_BOUNDING_BOX_LABELS.geometric,
      color: DEBUG_BOUNDING_BOX_COLORS.geometric,
      points: rectToPolygon(geometric),
    },
    {
      kind: 'visual',
      label: DEBUG_BOUNDING_BOX_LABELS.visual,
      color: DEBUG_BOUNDING_BOX_COLORS.visual,
      points: rectToPolygon(drawable),
      dashArray: '5 3',
    },
    {
      kind: 'rotatedVisual',
      label: DEBUG_BOUNDING_BOX_LABELS.rotatedVisual,
      color: DEBUG_BOUNDING_BOX_COLORS.rotatedVisual,
      points: rotatedRectCorners(drawable, pivot, rotationDeg),
      dashArray: '2 2',
    },
    {
      kind: 'aabb',
      label: DEBUG_BOUNDING_BOX_LABELS.aabb,
      color: DEBUG_BOUNDING_BOX_COLORS.aabb,
      points: rectToPolygon(rotatedAabbOfRect(drawable, pivot, rotationDeg)),
      dashArray: '1 3',
    },
  ]
}

/** The rotation pivot, as a separate marker. */
export function debugPivotForShape(shape: ShapeData): Point {
  return rotationPivotOfShape(shape)
}

/**
 * A compact fingerprint of everything that changes any of the boxes above.
 *
 * The debug layer polls rather than subscribing (no single notification covers loads, undo,
 * multiplayer edits *and* live drags), so it needs a cheap way to skip re-rendering when nothing
 * actually moved. Kind configuration is included because it changes the visual inset ratios.
 */
export function debugBoundingBoxSignature(shapes: ShapeData[]): string {
  let signature = ''

  for (const shape of shapes) {
    if (shape.hidden === true) continue

    signature += `${shape.id}:${shape.x},${shape.y},${shape.width},${shape.height},${
      shape.rotation ?? 0
    },${shape.type},${shape.kindId ?? ''},${shape.sides ?? ''},${shape.inset ?? ''};`
  }

  return signature
}
